﻿<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[sizeClass, coverIsActive ? 'device-active--cover' : '']"
      :title="device.deviceName">
      <!-- The ring carries the position (P13) and the pin holds the cover's own
           drawing — the shade in its window, at the height it stands — never a
           glyph and never a number: the arc already says how far it has come. -->
      <ControlStatusRing :value="position" accent="var(--device-color-cover)">
        <div class="cover-pin-art">
          <CoverAnimation
            :kind="coverKind"
            :position="position ?? 0"
            :tilt="tilt"
            :material-color="coverColors.material"
            :frame-color="coverColors.frame" />
        </div>
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Card mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'card'">
    <!-- Card face (the shades page's grid): the cover drawn as the live SVG its
         kind calls for, its name and its reading. A tap opens the controls — a
         cover's travel is a choice, not a toggle — and the open / stop / position
         row only appears at the largest size. -->
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      class="cover-card-face"
      @click="cardTap.onClick"
      @pointerdown="cardTap.onPointerDown"
      @pointermove="cardTap.onPointerMove"
      @pointerup="cardTap.onPointerUp"
      @pointerleave="cardTap.onPointerLeave"
      @pointercancel="cardTap.onPointerCancel"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <!-- The window itself, from inside the room: the cover in its own RAL
             tone, the sash off the same code, and the view beyond the glass —
             the device's own photo (greyed at night) or, with no photo, the
             hub's live weather as its gradient. Dragging down the glass picks
             the position (right drag: the slats) and the release sends it. -->
        <CoverAnimation
          :kind="coverKind"
          :position="position ?? 0"
          :tilt="tilt"
          :moving="coverMoving"
          :material-color="coverColors.material"
          :frame-color="coverColors.frame"
          :background-url="deviceThumbnailUrl"
          :night="coverNight"
          interactive
          @target="setPosition"
          @tilt-target="setSlatPosition" />
      </template>

      <template v-if="cardSize !== 'min'" #value>
        <span class="cover-card-face__value">
          {{ position !== null ? position + ' %' : '—' }}
          <span v-if="tilt !== null" class="cover-card-face__tilt">{{ tt('tilt', 'nagib') }} {{ tilt }}%</span>
        </span>
      </template>

      <template v-if="cardSize === 'max'" #control>
        <div class="cover-controls cover-card-face__controls">
          <div class="cover-mini-btn" :title="tt('Open', 'Odpri')" @click.stop="handleOpen">
            <span class="material-symbols-outlined">expand_less</span>
          </div>
          <div
            class="device-button-bright-bar cover-pos-bar"
            :title="tt('Drag to position', 'Povleci za položaj')"
            @pointerdown.stop="onPosBarDown"
            @pointermove.stop="onPosBarMove"
            @pointerup.stop="onPosBarUp"
            @pointercancel.stop="onPosBarUp"
            @lostpointercapture.stop="onPosBarUp">
            <div
              class="device-button-bright-fill"
              :class="{ 'device-button-bright-fill--off': !coverIsActive }"
              :style="{ width: (position ?? 0) + '%' }"></div>
            <div v-if="posBarDragging" class="device-button-bright-label">{{ position ?? 0 }}%</div>
          </div>
          <div class="cover-mini-btn" :title="tt('Close', 'Zapri')" @click.stop="handleClose">
            <span class="material-symbols-outlined">expand_more</span>
          </div>
        </div>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <!-- Expanded: colSpan >= 2 — icon + name + position bar + controls -->
    <template v-if="(colSpan ?? 1) >= 2">
      <div
        class="w-full h-full flex flex-col justify-between p-2 rounded-xl select-none"
        :class="coverIsActive ? 'bg-blue-400/15 border border-blue-400/30' : 'bg-white/5 border border-white/10'">
        <div class="flex items-center gap-1 min-w-0">
          <span class="material-symbols-outlined text-2xl" :class="coverIsActive ? 'text-blue-400' : 'text-gray-400'">{{
            device.icon || 'roller_shades'
          }}</span>
          <span
            class="text-sm font-medium text-white truncate flex-1 cursor-pointer"
            @click="emit('open-control-modal')"
            >{{ displayName }}</span
          >
          <span class="text-sm text-gray-400 shrink-0">{{ position ?? 0 }}%</span>
          <div class="flex items-center gap-1 shrink-0 ml-1">
            <button
              @click.stop="handleOpen"
              :disabled="controlling"
              class="w-10 h-10 flex items-center justify-center rounded-lg text-green-400 hover:bg-green-400/20 active:scale-90 disabled:opacity-40">
              <span class="material-symbols-outlined text-2xl">expand_less</span>
            </button>
            <button
              @click.stop="handleStop"
              :disabled="controlling"
              class="w-10 h-10 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-400/20 active:scale-90 disabled:opacity-40">
              <span class="material-symbols-outlined text-2xl">stop</span>
            </button>
            <button
              @click.stop="handleClose"
              :disabled="controlling"
              class="w-10 h-10 flex items-center justify-center rounded-lg text-blue-400 hover:bg-blue-400/20 active:scale-90 disabled:opacity-40">
              <span class="material-symbols-outlined text-2xl">expand_more</span>
            </button>
          </div>
        </div>
        <div
          class="w-full h-6 bg-white/10 rounded-full overflow-hidden cursor-pointer"
          @pointerdown.stop="onPosBarDown"
          @pointermove.stop="onPosBarMove"
          @pointerup.stop="onPosBarUp"
          @pointercancel.stop="onPosBarUp">
          <div
            class="h-full bg-blue-400 rounded-full transition-all duration-150"
            :style="{ width: (position ?? 0) + '%' }"></div>
        </div>
      </div>
    </template>
    <!-- Minimal: 1-wide -->
    <template v-else>
      <DeviceGridCell
        :icon="device.icon || 'roller_shades'"
        :name="device.deviceName"
        :is-on="coverIsActive"
        active-color="blue-400"
        @toggle="emit('open-control-modal')"
        @long-press="emit('open-control-modal')" />
    </template>
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'control'">
    <!-- The body of the sheet: the host owns the head and the panel, the body
         owns the hero and the rows (no head, no outer padding — the host's body
         container carries it). The cover's own colour is handed down while the
         cover is off its stop (the rule the head's ring follows); otherwise the
         host's accent stands. -->
    <div class="cover-sheet" :style="coverIsActive ? { '--sheet-accent': 'var(--device-color-cover)' } : {}">
      <!-- HERO — the cover itself: the kind-aware drawing the tiles show, at
           sheet size, looking out of its own window. The device photo is
           dropped here (owner decision); the position reads under the drawing.
           It is the same control the tiles carry: drag it to pick a position. -->
      <div class="cover-hero">
        <div class="cover-hero-figure" :class="{ 'cover-hero-figure--active': coverIsActive }">
          <CoverAnimation
            :kind="coverKind"
            :position="position ?? 0"
            :tilt="tilt"
            :moving="coverMoving"
            :material-color="coverColors.material"
            :frame-color="coverColors.frame"
            :night="coverNight"
            interactive
            @target="setPosition"
            @tilt-target="setSlatPosition" />
        </div>
        <div class="cover-hero-value">{{ position ?? 0 }}<small class="cover-hero-unit">%</small></div>
        <div class="cover-hero-caption">{{ tt('Position', 'Položaj') }}</div>
      </div>

      <!-- Stop row — open / stop / close in the drawn set, on the widget's own
           handlers and its busy state. -->
      <div class="cover-stops">
        <button
          type="button"
          class="cover-stop-btn cover-stop-btn--accent"
          :disabled="controlling"
          :title="tt('Open', 'Odpri')"
          @click="handleOpen">
          <ControlIcon name="cover-open" size="1.5rem" />
        </button>
        <button
          type="button"
          class="cover-stop-btn"
          :disabled="controlling"
          :title="tt('Stop', 'Ustavi')"
          @click="handleStop">
          <ControlIcon name="stop" size="1.5rem" />
        </button>
        <button
          type="button"
          class="cover-stop-btn cover-stop-btn--accent"
          :disabled="controlling"
          :title="tt('Close', 'Zapri')"
          @click="handleClose">
          <ControlIcon name="cover-close" size="1.5rem" />
        </button>
      </div>

      <!-- Position — the horizontal slider (0–100 exact, writing through the
           widget's own `setPosition` as the drag moves) with the five preset
           chips carrying the reached tint. -->
      <div class="cover-position">
        <ControlSlider :model-value="position ?? 0" @update:model-value="setPosition" />
        <div class="cover-presets">
          <ControlChip
            v-for="pct in POSITION_PRESETS"
            :key="pct"
            :mid="pct < (position ?? 0)"
            :active="position === pct"
            :disabled="controlling"
            @click="setPosition(pct)">
            {{ pct }} %
          </ControlChip>
        </div>
      </div>

      <!-- Blinds — venetian only (a cover that reports a tilt value): tap an
           arrow to step the slats, hold it to run the blind, release to stop. -->
      <div v-if="tilt !== null" class="cover-blinds">
        <div class="cover-row-head">
          <span class="cover-row-key">
            <ControlIcon name="tilt" size="0.9375rem" />
            {{ tt('Blinds', 'Žaluzije') }}
          </span>
          <span class="cover-row-value">{{ tilt }} %</span>
        </div>
        <div class="cover-blinds-row">
          <button
            type="button"
            class="cover-blind-btn"
            :class="{ 'cover-blind-btn--held': blindHold === -1 }"
            :title="tt('Blind Up — hold to move', 'Žaluzija gor — drži za pomik')"
            :aria-label="tt('Blind Up', 'Žaluzija gor')"
            @pointerdown.stop="onBlindPress(-1, $event)"
            @mousedown.stop="onBlindPress(-1)"
            @pointerup.stop="onBlindRelease"
            @mouseup.stop="onBlindRelease"
            @pointerleave="onBlindRelease"
            @mouseleave="onBlindRelease"
            @pointercancel="onBlindRelease">
            <ControlIcon name="rotate-ccw" size="1.375rem" />
          </button>
          <div class="cover-blinds-slider">
            <ControlSlider
              :model-value="tilt ?? 0"
              :title="tt('Position Blind', 'Položaj žaluzij')"
              :aria-label="tt('Position Blind', 'Položaj žaluzij')"
              @update:model-value="setSlatPosition" />
          </div>
          <button
            type="button"
            class="cover-blind-btn"
            :class="{ 'cover-blind-btn--held': blindHold === 1 }"
            :title="tt('Blind Down — hold to move', 'Žaluzija dol — drži za pomik')"
            :aria-label="tt('Blind Down', 'Žaluzija dol')"
            @pointerdown.stop="onBlindPress(1, $event)"
            @mousedown.stop="onBlindPress(1)"
            @pointerup.stop="onBlindRelease"
            @mouseup.stop="onBlindRelease"
            @pointerleave="onBlindRelease"
            @mouseleave="onBlindRelease"
            @pointercancel="onBlindRelease">
            <ControlIcon name="rotate-cw" size="1.375rem" />
          </button>
        </div>
      </div>

      <!-- Shade automation — the overlay's own states (wind / rain / sun) as
           the sheet's meta chips. -->
      <div v-if="hasShadeAutomations" class="cover-automations">
        <ControlChip
          v-if="windState !== null"
          small
          :active="windState === 'active'"
          :mid="windState === 'resuming'"
          :title="tt('Wind protection', 'Zaščita pred vetrom')">
          <ControlIcon name="wind" size="1.0625em" />
          {{ tt('Wind', 'Veter') }} · {{ stateWord(windState) }}
        </ControlChip>
        <ControlChip
          v-if="rainState !== null"
          small
          :active="rainState === 'active'"
          :mid="rainState === 'resuming'"
          :title="tt('Rain protection', 'Zaščita pred dežjem')">
          <ControlIcon name="drop" size="1.0625em" />
          {{ tt('Rain', 'Dež') }} · {{ stateWord(rainState) }}
        </ControlChip>
        <ControlChip
          v-if="sunState !== null"
          small
          :active="sunState === 'active'"
          :title="tt('Sun tracking', 'Sončno vodenje')">
          <ControlIcon name="warm" size="1.0625em" />
          {{ tt('Sun', 'Sonce') }} · {{ stateWord(sunState) }}
        </ControlChip>
      </div>

      <!-- Sun controller automation widget (sun info + mode switcher) -->
      <SunTrackerPanel :device-id="device.id" />

      <!-- Cover external control automation widget -->
      <CoverAutomationPanel :device-id="device.id" />

      <!-- Automation boolean timelines (rain/wind protection + sun controller) -->
      <SetpointTimeline
        v-if="timelineValues.length"
        :device-values="timelineValues"
        :device-id="device.id"
        accent-color="rgb(59, 130, 246)"
        inactive-color="transparent"
        :transparent-track="true" />

      <!-- Value history logs. The heading's glyph is the drawn set's
           `activity` (P11 sweep, card t_bd3aea71). -->
      <div v-if="chartValues.length" class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p
          class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <ControlIcon name="activity" size="0.875rem" />
          {{ tt('History', 'Zgodovina') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <ValueLogButton
            v-for="v in chartValues"
            :key="v.id"
            :device-value="v"
            :device="{ id: device.id, deviceName: device.deviceName }" />
        </div>
      </div>

      <!-- Meta — last updated, over the sheet's hairline. -->
      <div v-if="lastUpdated" class="cover-meta">
        <span class="cover-meta-left">
          <ControlIcon name="clock" size="0.8125rem" />
          {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
        </span>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { formatTime } from '../../utils/formatDate';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import SunTrackerPanel from './sun/SunTrackerPanel.vue';
import CoverAutomationPanel from './cover/CoverAutomationPanel.vue';
import { getDefaultThumbnailUrl } from '../../composables/useDeviceThumbnail';
import ValueLogButton from './ValueLogButton.vue';
import SetpointTimeline from './SetpointTimeline.vue';
import ControlSlider from '../device-sheet/ControlSlider.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import { tt } from '../../utils/consoleText';
import { coverKindOf, toFiniteNumber } from './deviceWidgetTypes';
import { useGridTap } from '../../composables/useGridTap';
import { useCoverNight } from '../../composables/useCoverNight';
import { coverColorsOf } from '@/utils/coverColors';
import CoverAnimation from './cover/CoverAnimation.vue';
import DeviceCardShell from './DeviceCardShell.vue';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  colSpan?: number;
  rowSpan?: number;
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────

const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty, findValueByKeys } = useDeviceControl();

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

const position = computed<number | null>(() => {
  const key = ['position_status', 'position', 'level'].find((k) => k in currentValues.value);
  if (key) return toFiniteNumber(currentValues.value[key]) ?? 0;
  const dv = findValueByKeys(props.device, ['position_status', 'position', 'level']);
  return toFiniteNumber(dv?.currentValue);
});

/** Slat keys the hub publishes for a venetian blind — mirrors `stores/shades.ts`. */
const TILT_VALUE_KEYS = ['tilt', 'slat', 'slat_position', 'orientation', 'angle'];

const tilt = computed<number | null>(() => {
  // The slat tilt a blind reports (0 = open, 100 = shut). Any cover that publishes
  // one of these can tilt — the type hint only says it *can*, so the value decides,
  // not the type string.
  const live = Object.keys(currentValues.value).find((k) => TILT_VALUE_KEYS.includes(k.toLowerCase()));
  const liveValue = live ? toFiniteNumber(currentValues.value[live]) : null;
  if (liveValue !== null) return Math.round(liveValue);
  const dv = props.device.deviceValues?.find((v) => TILT_VALUE_KEYS.includes(String(v.valueKey).toLowerCase()));
  const value = toFiniteNumber(dv?.currentValue);
  return value !== null ? Math.round(value) : null;
});

const coverIsActive = computed(() => position.value !== null && position.value > 0);
const loggableValues = computed(() =>
  (props.device.deviceValues ?? []).filter((v) => v.loggingType && v.loggingType !== 'None'),
);

const PROTECTION_LOG_KEYS = ['shade_rain_protection__protection_active', 'shade_wind_protection__protection_active'];

const SUN_LOG_KEYS = [
  'shade_sun_tracking__enabled',
  'shade_sun_tracking__sun_active',
  'shade_sun_tracking__sun_in_window',
];

/** Momentary command values never shown as history cards. */
const COMMAND_EXCLUDE_KEYS = ['updown', 'stop'];

/** Automation boolean state values → shown as boolean timelines (SetpointTimeline). */
const timelineValues = computed(() =>
  (props.device.deviceValues ?? []).filter(
    (v) =>
      v.loggingType &&
      v.loggingType !== 'None' &&
      [...PROTECTION_LOG_KEYS, ...SUN_LOG_KEYS].includes(String(v.valueKey)),
  ),
);

/** Remaining logged values → shown as chart cards (excludes commands + automation booleans). */
const chartValues = computed(() =>
  loggableValues.value.filter(
    (v) =>
      !PROTECTION_LOG_KEYS.includes(String(v.valueKey)) &&
      !SUN_LOG_KEYS.includes(String(v.valueKey)) &&
      !COMMAND_EXCLUDE_KEYS.includes(String(v.valueKey).toLowerCase()),
  ),
);

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

// The card's background picture: the device's own thumbnail, resolved cloud-safely.
const deviceThumbnailUrl = computed(() => getDefaultThumbnailUrl(props.device));

// Which graphic the icon circle draws (the hub reports every shade as `cover`)
const coverKind = computed(() => coverKindOf(props.device));

// ── The cover's own colours, and the view beyond its glass ───────────────────
//
// The lamella takes the device's stored RAL tone (the one the Home 3D view
// paints the real blind with) and the sash a lighter step off it; the window
// looks out on the hub's live weather, or on the device's own photo — greyed
// after sunset, in colour by day.
const coverColors = computed(() => coverColorsOf(props.device));
const coverNight = useCoverNight();

/** True for a couple of seconds after the position moved — the panel then animates. */
const coverMoving = ref(false);
let coverMoveTimer: number | null = null;
watch(position, (next, prev) => {
  if (next === null || prev === null || next === prev) return;
  coverMoving.value = true;
  if (coverMoveTimer !== null) clearTimeout(coverMoveTimer);
  coverMoveTimer = window.setTimeout(() => {
    coverMoving.value = false;
  }, 2500);
});

// Control mode actions
function setPosition(pct: number) {
  setProperty(props.device, 'position', pct);
}

const posBarDragging = ref(false);
function onPosBarDown(e: PointerEvent) {
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  posBarDragging.value = true;
  applyPosBar(e);
}
function onPosBarMove(e: PointerEvent) {
  if (!posBarDragging.value) return;
  applyPosBar(e);
}
function onPosBarUp() {
  posBarDragging.value = false;
}
function applyPosBar(e: PointerEvent) {
  const el = e.currentTarget as HTMLElement;
  const rect = el.getBoundingClientRect();
  const pct = Math.max(0, Math.min(100, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
  setPosition(pct);
}
async function handleOpen() {
  await setProperty(props.device, 'updown', false);
}
async function handleClose() {
  await setProperty(props.device, 'updown', true);
}
async function handleStop() {
  await setProperty(props.device, 'stop', true);
  setTimeout(() => setProperty(props.device, 'stop', false), 100);
}

// ── Control mode (the sheet body) ────────────────────────────────────────
/** The five preset chips the sketch shows (0–100 %, reached-tint). */
const POSITION_PRESETS = [0, 25, 50, 75, 100];

/** One tap on a blinds arrow steps the slats by this much (of 0–100 %). */
const SLAT_STEP = 10;
/** A press shorter than this is a step; longer is a hold (the light's shape). */
const BLIND_HOLD_ARM_MS = 350;
/** While a hold is armed the move is re-asserted on the light's dim-hold cadence. */
const BLIND_HOLD_REPEAT_MS = 150;

/** The slat slider writes the live `tilt` value (0–100: 0 = open, 100 = shut, exact). */
async function setSlatPosition(pct: number): Promise<void> {
  await setProperty(props.device, 'tilt', pct);
}

/** Step the slats one step from the live value, clamped to the register. */
async function stepSlats(direction: 1 | -1): Promise<void> {
  const current = tilt.value;
  if (current === null) return;
  const next = Math.max(0, Math.min(100, current + direction * SLAT_STEP));
  if (next !== current) await setProperty(props.device, 'tilt', next);
}

// The blinds arrows: tap = step the slats, hold = move the blind and release =
// stop. The shape is the light's dim-hold (`setPointerCapture` + the double-
// fire guard), with the arm delay that separates a tap from a hold.
/** 0 = idle, -1 = the up arrow is held, 1 = the down arrow is held. */
const blindHold = ref<0 | 1 | -1>(0);
let blindHoldArmTimer: number | null = null;
let blindHoldRepeatTimer: number | null = null;

function onBlindPress(direction: 1 | -1, event?: PointerEvent): void {
  if (blindHold.value !== 0) return; // one physical press fires pointerdown AND mousedown
  blindHold.value = direction;
  // Capture the pointer: the button's active shrink (or a small drift) would
  // otherwise fire pointerleave and cut the hold — the Electron hold regression.
  if (event && event.currentTarget) {
    try {
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    } catch {
      /* noop */
    }
  }
  blindHoldArmTimer = window.setTimeout(() => {
    blindHoldArmTimer = null;
    // The hold: run the blind. `false` = up/open (the rotate-ccw arrow),
    // `true` = down/close (rotate-cw) — the polarity `handleOpen`/`handleClose`
    // already write; re-asserted while the button is held (the light's cadence).
    const move = () => void setProperty(props.device, 'updown', direction === 1);
    move();
    blindHoldRepeatTimer = window.setInterval(move, BLIND_HOLD_REPEAT_MS);
  }, BLIND_HOLD_ARM_MS);
}

function onBlindRelease(): void {
  const held = blindHold.value;
  if (held === 0) return; // pointerup + pointercancel both fire for one release
  blindHold.value = 0;
  if (blindHoldArmTimer !== null) {
    // Released before the arm: a tap steps the slats (a tap must not fire a move).
    window.clearTimeout(blindHoldArmTimer);
    blindHoldArmTimer = null;
    void stepSlats(held);
    return;
  }
  if (blindHoldRepeatTimer !== null) {
    window.clearInterval(blindHoldRepeatTimer);
    blindHoldRepeatTimer = null;
  }
  // Released after the arm: the blind was moving — the stop pulse ends it.
  void handleStop();
}

// A hold must not outlive the sheet it lives in.
onBeforeUnmount(() => {
  if (blindHold.value === 0) return;
  const wasMoving = blindHoldArmTimer === null;
  blindHold.value = 0;
  if (blindHoldArmTimer !== null) {
    window.clearTimeout(blindHoldArmTimer);
    blindHoldArmTimer = null;
  }
  if (blindHoldRepeatTimer !== null) {
    window.clearInterval(blindHoldRepeatTimer);
    blindHoldRepeatTimer = null;
  }
  if (wasMoving) void handleStop();
});

/** The state word a shade-automation chip prints (`ShadeAutomationIndicator`'s data). */
function stateWord(state: 'active' | 'resuming' | 'idle' | null): string {
  if (state === 'active') return tt('active', 'aktivno');
  if (state === 'resuming') return tt('waiting', 'čakanje');
  return tt('standby', 'v pripravljenosti');
}

// Card face: a tap opens the controls — a cover's travel is a choice, not a
// toggle — and a hold opens them at once rather than waiting for the release.
const cardTap = useGridTap(
  () => emit('open-control-modal'),
  () => emit('open-control-modal'),
);

// ── Shade automation state ───────────────────────────────────────────────
const hasWindProtection = computed(() => 'shade_wind_protection__protection_active' in currentValues.value);
const hasRainProtection = computed(() => 'shade_rain_protection__protection_active' in currentValues.value);
const hasSunTracking = computed(() => 'shade_sun_tracking__sun_active' in currentValues.value);
const hasShadeAutomations = computed(() => hasWindProtection.value || hasRainProtection.value || hasSunTracking.value);

const windState = computed((): 'active' | 'resuming' | 'idle' | null => {
  if (!hasWindProtection.value) return null;
  const active = currentValues.value['shade_wind_protection__protection_active'];
  if (active === true || active === 1 || active === 'true') return 'active';
  const resume = currentValues.value['shade_wind_protection__resume_time'];
  if (resume) return 'resuming';
  return 'idle';
});

const rainState = computed((): 'active' | 'resuming' | 'idle' | null => {
  if (!hasRainProtection.value) return null;
  const active = currentValues.value['shade_rain_protection__protection_active'];
  if (active === true || active === 1 || active === 'true') return 'active';
  const resume = currentValues.value['shade_rain_protection__resume_time'];
  if (resume) return 'resuming';
  return 'idle';
});

const sunState = computed((): 'active' | 'idle' | null => {
  if (!hasSunTracking.value) return null;
  const v = currentValues.value['shade_sun_tracking__sun_active'];
  return v === true || v === 1 || v === 'true' ? 'active' : 'idle';
});

// ── Display ──────────────────────────────────────────────────────────────
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));
</script>

<style scoped>
/* ── Icon mode: the ring's clear circle holds the cover's own drawing ── */
.cover-pin-art {
  display: flex;
  align-items: center;
  justify-content: center;
  /* A definite box inside the ring's arc, and a percentage so it follows every
     pin size (sm/md/lg/fluid). The drawing brings its own margin — the recess
     around the window — so it can take more of the circle than a bare glyph. */
  width: 76%;
  height: 76%;
}

/* ── Button mode: same stacked layout as the light / thermostat tiles ── */
.cover-layout {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  margin-top: 0;
  min-height: 0;
}

.cover-button-head {
  display: flex;
  align-items: center;
  gap: 0.55em;
  min-width: 0;
}

/* The icon/photo circle: a window with the cover drawn in it, cancel target on tap */
.cover-icon-circle {
  width: 2.35em;
  height: 2.35em;
  margin-bottom: 0;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
  color: rgb(148 163 184);
  background: var(--icon-circle-fill);
  border: 1px solid color-mix(in srgb, currentColor 38%, transparent);
  transition:
    color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.cover-active .cover-icon-circle {
  color: var(--device-color-cover);
  box-shadow:
    0 0 10px color-mix(in srgb, currentColor 30%, transparent),
    0 0 24px color-mix(in srgb, currentColor 12%, transparent);
}

/* Device photo (its image gallery) fills the circle when the device has one */
.cover-icon-circle :deep(.device-thumb) {
  width: 100%;
  height: 100%;
}

/* The illustration breathes inside the ring — the same small inset the glyphs
   get in the other tiles, so the drawn window never touches the border. */
.cover-icon-circle :deep(.cover-anim) {
  width: 86%;
  height: 86%;
}

.cover-button-text {
  display: flex;
  flex-direction: column;
  gap: 0.1em;
  min-width: 0;
}

/* Name above the value, single line — same scale as the light / thermostat tiles */
.cover-layout .device-name {
  font-size: 0.62em;
  min-width: 0;
}

.cover-button-value {
  display: flex;
  align-items: baseline;
  gap: 0.4em;
  min-width: 0;
  font-size: var(--tile-status-size);
  font-weight: 700;
  line-height: 1;
  color: rgb(17, 24, 39);
}

/* The slat tilt a venetian blind reports next to its position */
.cover-tilt {
  font-size: 0.72em;
  font-weight: 600;
  opacity: 0.75;
  white-space: nowrap;
}

.cover-active .device-name,
.cover-active .device-status,
.cover-active .cover-button-value {
  color: var(--device-color-cover) !important;
}
.cover-active {
  border-color: var(--device-color-cover) !important;
}

/* Position bar in button mode (shared bar styles, scoped copy) */
.device-button-bright-bar {
  width: 100%;
  height: 1.35em;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  margin-top: 0.2em;
  cursor: pointer;
  touch-action: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.device-button-bright-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: 0;
  transition: width 0.15s ease-out;
  opacity: 0.85;
}
.device-button-bright-fill--off {
  opacity: 0.3;
  filter: grayscale(0.7);
}
.device-button-bright-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  pointer-events: none;
  letter-spacing: 0.03em;
}
.cover-pos-bar .device-button-bright-fill {
  background: var(--device-color-cover, rgb(96, 165, 250));
}

/* Open / position bar / close — the row that drives the cover, pinned to the tile
   bottom with the same breathing gap the light and thermostat tiles use. */
.cover-controls {
  display: flex;
  align-items: center;
  gap: 0.35em;
  margin-top: auto;
  padding-top: 0.45em;
}

.cover-controls .cover-pos-bar {
  flex: 1 1 auto;
  min-width: 0;
  margin-top: 0;
}

.cover-mini-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.45em;
  height: 1.45em;
  border-radius: 999px;
  flex-shrink: 0;
  background: color-mix(in srgb, var(--device-color-cover, rgb(96, 165, 250)) 14%, transparent);
  color: var(--device-color-cover, rgb(96, 165, 250));
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    transform 0.15s ease;
}
.cover-mini-btn:hover {
  background: color-mix(in srgb, var(--device-color-cover, rgb(96, 165, 250)) 28%, transparent);
}
.cover-mini-btn:active {
  transform: scale(0.92);
}
.cover-mini-btn .material-symbols-outlined {
  font-size: 1em;
  line-height: 1;
}

/* ── Control mode ── */
/* The app themes by the `.dark` class on <html>, not the OS preference */
.dark .cover-button-value {
  color: rgb(255, 255, 255);
}

/* ── Control mode: the sheet body ───────────────────────────────────────── */
/*
 * The cover sheet in the control-sheet language: the drawing hero, the stop
 * row, the horizontal position slider with its preset chips and the blinds
 * row. The accent these rows read is `--sheet-accent` — the body root hands
 * the cover's own colour down while the cover is off its stop (the rule the
 * head's ring follows) and leaves the host's accent standing otherwise.
 */
.cover-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
  min-width: 0;
}

/* The hero: the drawing at the sketch's window size, the position under it. */
.cover-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding-top: 0.25rem;
}

.cover-hero-figure {
  width: 14rem;
  height: 14rem;
  color: rgb(148, 163, 184);
  transition: color 0.3s ease;
}

.cover-hero-figure--active {
  color: var(--device-color-cover, rgb(96, 165, 250));
}

.cover-hero-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.cover-hero-unit {
  margin-left: 0.15em;
  font-size: 1.0625rem;
  font-weight: 600;
  color: rgb(var(--gray-500));
}

.dark .cover-hero-unit {
  color: rgb(var(--gray-400));
}

.cover-hero-caption {
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
  text-align: center;
}

.dark .cover-hero-caption {
  color: rgb(var(--gray-400));
}

/* The stop row: open / stop / close in the stepper family's round buttons. */
.cover-stops {
  display: flex;
  justify-content: center;
  gap: calc(0.75rem * var(--ui-space-factor));
}

.cover-stop-btn {
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1.5px solid var(--glass-edge);
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-600));
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s,
    transform 0.1s;
}

.dark .cover-stop-btn {
  background: rgba(255, 255, 255, 0.03);
  color: rgb(var(--gray-300));
}

.cover-stop-btn--accent {
  border-color: color-mix(in srgb, var(--sheet-accent, var(--device-color-cover)) 55%, transparent);
  background: color-mix(in srgb, var(--sheet-accent, var(--device-color-cover)) 13%, transparent);
  color: var(--sheet-accent, var(--device-color-cover));
}

.cover-stop-btn:active:not(:disabled) {
  transform: scale(0.94);
}

.cover-stop-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Position: the horizontal slider over the five preset chips. */
.cover-position {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.cover-presets {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: calc(0.5rem * var(--ui-space-factor));
}

/* Blinds (venetian): caption row over [step arrow | slat slider | step arrow]. */
.cover-blinds {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.cover-row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
  font-size: 0.8125rem;
}

.cover-row-key {
  display: inline-flex;
  align-items: center;
  gap: 0.375em;
  font-weight: 550;
  color: rgb(var(--gray-500));
}

.dark .cover-row-key {
  color: rgb(var(--gray-400));
}

.cover-row-value {
  font-weight: 650;
  color: rgb(var(--gray-800));
  font-variant-numeric: tabular-nums;
}

.dark .cover-row-value {
  color: rgb(var(--gray-100));
}

.cover-blinds-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
}

.cover-blinds-slider {
  flex: 1 1 auto;
  min-width: 0;
}

/* The step arrows: the hold gesture owns them, so the sheet must not scroll
   from here (the light's dim pair carries the same rule). */
.cover-blind-btn {
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1.5px solid var(--glass-edge);
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-600));
  cursor: pointer;
  touch-action: none;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s,
    transform 0.1s;
}

.dark .cover-blind-btn {
  background: rgba(255, 255, 255, 0.03);
  color: rgb(var(--gray-300));
}

.cover-blind-btn--held {
  border-color: color-mix(in srgb, var(--sheet-accent, var(--device-color-cover)) 55%, transparent);
  background: color-mix(in srgb, var(--sheet-accent, var(--device-color-cover)) 13%, transparent);
  color: var(--sheet-accent, var(--device-color-cover));
}

.cover-blind-btn:active {
  transform: scale(0.94);
}

/* The shade-automation meta chips. */
.cover-automations {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: calc(0.5rem * var(--ui-space-factor));
}

/* The meta row: last updated, over the sheet's hairline. */
.cover-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding-top: 0.375rem;
  border-top: 1px solid var(--glass-edge-soft);
  font-size: 0.71875rem;
  color: rgb(var(--gray-500));
}

.dark .cover-meta {
  color: rgb(var(--gray-400));
}

.cover-meta-left {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

/* ── Card face (the grid the shades page draws) ─────────────────────────────
   The shell owns the frame, the name and the size rules; these are the cover's
   own parts inside it: the live drawing, the reading and the drive row. */
.cover-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #64748b;
}

.dark .cover-card-face__value {
  color: #94a3b8;
}

.cover-card-face.device-card--on .cover-card-face__value {
  color: #0369a1;
}

.dark .cover-card-face.device-card--on .cover-card-face__value {
  color: #7dd3fc;
}

.cover-card-face__tilt {
  margin-left: 0.3rem;
  font-size: 0.85em;
  font-weight: 500;
  opacity: 0.75;
}

.cover-card-face__controls {
  width: 100%;
}

/* The position bar takes what the two verbs leave. */
.cover-card-face__controls .cover-pos-bar {
  width: auto;
  flex: 1 1 auto;
  min-width: 0;
}
</style>
